/**
 * Emit hosts for the `chat` analytics group — 6 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type ChatCreatedEventProps,
  type ChatDeletedEventProps,
  type ChatViewedEventProps,
} from '@/models/analytics/features/chat';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';

/**
 * Track when a new Evidence chat is created (first message sent to new chat).
 */
export function trackChatCreated(input: ChatCreatedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ChatCreated,
    props: input,
  });
}

/**
 * Track when an Evidence chat is successfully deleted.
 */
export function trackChatDeleted(input: ChatDeletedEventProps) {
  track({
    event_name: TrackingEventName.ChatDeleted,
    props: input,
  });
}

export function trackChatMessageSubmitted(input: {
  sessionId: string;
  mode: string;
  message_length: number;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Chat,
    props: {
      action: SegmentActionName.ChatMessageSubmitted,
      sessionId: input.sessionId,
      mode: input.mode,
      message_length: input.message_length,
    },
  });
}

export function trackChatModeToggled(input: {
  sessionId: string;
  previous_mode: string;
  new_mode: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Chat,
    props: {
      action: SegmentActionName.ChatModeToggled,
      sessionId: input.sessionId,
      previous_mode: input.previous_mode,
      new_mode: input.new_mode,
    },
  });

  // V2 — Amplitude migration (chatModeToggled -> feature_toggled)
  track({
    event_name: TrackingEventName.FeatureToggled,
    props: {
      toggle_action: 'chat_mode_toggled',
      session_id: input.sessionId,
      previous_mode: input.previous_mode,
      new_mode: input.new_mode,
    },
  });
}

export function trackChatStreamInterrupted(input: {
  sessionId: string;
  messageId?: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Chat,
    props: {
      action: SegmentActionName.ChatStreamInterrupted,
      sessionId: input.sessionId,
    },
  });
  // V2 — Amplitude migration (chatStreamInterrupted -> message_stopped)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.MessageStopped,
    props: {
      session_id: input.sessionId,
      message_id: input.messageId,
    },
  });
}

/**
 * Track when an existing Evidence chat is viewed/loaded.
 */
export function trackChatViewed(input: ChatViewedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ChatViewed,
    props: input,
  });
}
